import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formatStandard } from '../utils/standardLabel';
import './Cart.css';

const Cart = () => {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [paymentFailureMessage, setPaymentFailureMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchCart();
    
    // Check for payment failure in URL params
    const failed = searchParams.get('payment_failed') === 'true';
    const message = searchParams.get('message') || 'Payment failed. Please try again.';
    
    if (failed) {
      setPaymentFailed(true);
      setPaymentFailureMessage(decodeURIComponent(message));
      
      // Clear cart on frontend (backend already cleared it)
      setCart([]);
      
      // Dispatch event to update cart badge in header
      window.dispatchEvent(new Event('cartUpdated'));
      
      // Clear URL params after showing message
      const timer = setTimeout(() => {
        setSearchParams({}, { replace: true });
        setPaymentFailed(false);
        setPaymentFailureMessage('');
        // Refresh the page to ensure clean state
        window.location.reload();
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cart');
      setCart(response.data.cart);
      setError('');
    } catch (err) {
      const errorMessage = err.userFriendlyMessage || 'Failed to load cart. Please try again.';
      setError(errorMessage);
      console.error(err);
      
      // Redirect to schools page on error
      setTimeout(() => {
        navigate('/schools');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (cartId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(cartId);
      return;
    }

    setUpdatingItems(prev => new Set(prev).add(cartId));
    try {
      await api.put(`/cart/${cartId}`, { quantity: newQuantity });
      fetchCart();
      // Dispatch event to update cart badge in header
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update quantity');
    } finally {
      setUpdatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(cartId);
        return newSet;
      });
    }
  };

  const removeItem = async (cartId) => {
    if (!window.confirm('Are you sure you want to remove this item from your cart?')) {
      return;
    }
    
    setUpdatingItems(prev => new Set(prev).add(cartId));
    try {
      await api.delete(`/cart/${cartId}`);
      fetchCart();
      // Dispatch event to update cart badge in header
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove item');
    } finally {
      setUpdatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(cartId);
        return newSet;
      });
    }
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      return total + parseFloat(item.price) * item.quantity;
    }, 0);
  };

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div className="cart-page">
          <div className="container">
            <div className="cart-loading">
              <div className="loading-spinner"></div>
              <p>Loading your cart...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="cart-page">
        <div className="container">
          <div className="cart-header">
            <div className="cart-header-content">
              <h1 className="cart-page-title">
                <span className="cart-icon">🛒</span>
                Shopping Cart
              </h1>
              <p className="cart-subtitle">
                {cart.length === 0 
                  ? 'Your cart is waiting for you' 
                  : `${cart.length} ${cart.length === 1 ? 'item' : 'items'} in your cart`}
              </p>
            </div>
          </div>

          {paymentFailed && (
            <div className="cart-error" style={{ 
              backgroundColor: '#f8d7da', 
              borderColor: '#f5c6cb',
              color: '#721c24',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <div>
                <strong style={{ display: 'block', marginBottom: '4px' }}>Payment Failed</strong>
                <span>{paymentFailureMessage}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="cart-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{error}</span>
            </div>
          )}

          {cart.length > 0 && (
            <div className="cart-info-notification">
              <div className="info-icon-wrapper">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </div>
              <div className="info-content">
                <strong>Important:</strong> Only one kit can be ordered per student per standard.
              </div>
            </div>
          )}

          {cart.length === 0 ? (
            <div className="cart-empty-state">
              <div className="empty-cart-icon">
                <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                </svg>
              </div>
              <h2>Your cart is empty</h2>
              <p>Looks like you haven't added anything to your cart yet. Start shopping to fill it up!</p>
              <Link to="/schools" className="btn-cart-primary">
                <span>Browse Schools</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14"></path>
                  <path d="M12 5l7 7-7 7"></path>
                </svg>
              </Link>
            </div>
          ) : (
            <div className="cart-content-wrapper">
              <div className="cart-items-section">
                {cart.map((item, index) => (
                  <div 
                    key={item.id} 
                    className={`cart-item-card ${updatingItems.has(item.id) ? 'updating' : ''}`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="cart-item-main">
                      <div className="cart-item-badge">
                        <span className="badge-text">{formatStandard(item.standard)}</span>
                      </div>
                      <div className="cart-item-details">
                        <h3 className="cart-item-title">{item.kit_name}</h3>
                        <div className="cart-item-meta">
                          <div className="meta-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                              <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                            <span>{item.school_name}</span>
                          </div>
                        </div>
                        <div className="cart-item-price-info">
                          <span className="price-label">Price per kit:</span>
                          <span className="price-value">₹{parseFloat(item.price).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="cart-item-controls">
                      <div className="quantity-section">
                        <label className="quantity-label">Quantity</label>
                        <div className="quantity-controls-modern">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="quantity-btn quantity-btn-minus"
                            disabled={updatingItems.has(item.id)}
                            aria-label="Decrease quantity"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                          </button>
                          <div className="quantity-display">
                            <span className="quantity-number">{item.quantity}</span>
                          </div>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="quantity-btn quantity-btn-plus"
                            disabled={updatingItems.has(item.id)}
                            aria-label="Increase quantity"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <line x1="12" y1="5" x2="12" y2="19"></line>
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="item-total-section">
                        <span className="item-total-label">Item Total</span>
                        <span className="item-total-amount">
                          ₹{(parseFloat(item.price) * item.quantity).toFixed(2)}
                        </span>
                      </div>

                      <button
                        onClick={() => removeItem(item.id)}
                        className="remove-item-btn"
                        disabled={updatingItems.has(item.id)}
                        aria-label="Remove item"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                        <span>Remove</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="cart-summary-card">
                <div className="summary-header">
                  <h2 className="summary-title">Order Summary</h2>
                </div>
                
                <div className="summary-content">
                  <div className="summary-row">
                    <span className="summary-label">Subtotal</span>
                    <span className="summary-value">₹{calculateTotal().toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Delivery Charges</span>
                    <span className="summary-value summary-note">Calculated at checkout</span>
                  </div>
                  
                  <div className="summary-divider"></div>
                  
                  <div className="summary-total-row">
                    <span className="total-label">Total Amount</span>
                    <span className="total-amount">₹{calculateTotal().toFixed(2)}</span>
                  </div>
                </div>

                <div className="summary-footer">
                  <Link to="/checkout" className="checkout-btn">
                    <span>Proceed to Checkout</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14"></path>
                      <path d="M12 5l7 7-7 7"></path>
                    </svg>
                  </Link>
                  <Link to="/schools" className="continue-shopping-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 12H5"></path>
                      <path d="M12 19l-7-7 7-7"></path>
                    </svg>
                    <span>Continue Shopping</span>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Cart;
